import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import {
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  PrinterIcon,
  EnvelopeIcon,
  EyeIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import WorkOrderDetail from './WorkOrderDetail';
import PlotSearchModal from './PlotSearchModal';
import ContactSearchModal from './ContactSearchModal';

const WORK_ORDER_TYPES = [
  { value: 'funeral_preparation', label: 'Funeral Preparation' },
  { value: 'headstone_installation', label: 'Headstone Installation' },
  { value: 'repair', label: 'Repair' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'other', label: 'Other' }
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
];

const WorkOrders = () => {
  const { token, user } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [workOrders, setWorkOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(() => {
    const idParam = new URLSearchParams(window.location.search).get('id');
    return idParam ? parseInt(idParam) : null;
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', assignedTo: '', type: '', priority: '' });
  const [showFilters, setShowFilters] = useState(false);

  // Create form state
  const [newOrder, setNewOrder] = useState({
    title: '', description: '', workOrderType: 'other', priority: 'medium',
    assignedTo: '', dueDate: '',
    includeMap: true, notifyOnStatusChange: true
  });
  const [formPlots, setFormPlots] = useState([]);
  const [formContacts, setFormContacts] = useState([]);
  const [isPlotModalOpen, setIsPlotModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    fetchStatuses();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchWorkOrders();
  }, [page, filters, search]);

  const fetchStatuses = async () => {
    try {
      const response = await fetch('/api/work-orders/statuses', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) setStatuses(await response.json());
    } catch (error) { console.error('Error:', error); }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : data.users || []);
      }
    } catch (error) { console.error('Error:', error); }
  };

  const fetchWorkOrders = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (filters.status) params.append('status', filters.status);
      if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
      if (filters.type) params.append('type', filters.type);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await fetch(`/api/work-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkOrders(data.workOrders);
        setTotal(data.total);
        setTotalPages(data.totalPages);
      }
    } catch (error) { console.error('Error:', error); }
    finally { setIsLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newOrder.title) return;

    setIsLoading(true);
    try {
      const response = await fetch('/api/work-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newOrder,
          assignedTo: newOrder.assignedTo ? parseInt(newOrder.assignedTo) : null,
          plotIds: formPlots.map(p => p.fid),
          contactIds: formContacts.map(c => c.id)
        })
      });

      if (response.ok) {
        setShowCreateForm(false);
        setNewOrder({
          title: '', description: '', workOrderType: 'other', priority: 'medium',
          assignedTo: '', dueDate: '',
          includeMap: true, notifyOnStatusChange: true
        });
        setFormPlots([]);
        setFormContacts([]);
        fetchWorkOrders();
      }
    } catch (error) { console.error('Error:', error); }
    finally { setIsLoading(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this work order?')) return;
    try {
      await fetch(`/api/work-orders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWorkOrders();
    } catch (error) { console.error('Error:', error); }
  };

  if (selectedId) {
    return (
      <WorkOrderDetail
        workOrderId={selectedId}
        onBack={() => { setSelectedId(null); setSearchParams({}); fetchWorkOrders(); }}
        statuses={statuses}
        users={users}
      />
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold">Work Orders</h1>
          <p className="text-gray-600 text-sm">{total} total work orders</p>
        </div>
        {user?.role !== 'grounds_crew' && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700"
          >
            <PlusIcon className="h-4 w-4" />
            New Work Order
          </button>
        )}
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search work orders..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:ring-brand-green-500 focus:border-brand-green-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-md text-sm ${showFilters ? 'bg-brand-green-50 border-brand-green-300' : ''}`}
          >
            <FunnelIcon className="h-4 w-4" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t">
            <select
              value={filters.status}
              onChange={e => { setFilters(prev => ({ ...prev, status: e.target.value })); setPage(1); }}
              className="px-2 py-1.5 border rounded-md text-sm"
            >
              <option value="">All Statuses</option>
              {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select
              value={filters.assignedTo}
              onChange={e => { setFilters(prev => ({ ...prev, assignedTo: e.target.value })); setPage(1); }}
              className="px-2 py-1.5 border rounded-md text-sm"
            >
              <option value="">All Assignees</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
            </select>
            <select
              value={filters.type}
              onChange={e => { setFilters(prev => ({ ...prev, type: e.target.value })); setPage(1); }}
              className="px-2 py-1.5 border rounded-md text-sm"
            >
              <option value="">All Types</option>
              {WORK_ORDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select
              value={filters.priority}
              onChange={e => { setFilters(prev => ({ ...prev, priority: e.target.value })); setPage(1); }}
              className="px-2 py-1.5 border rounded-md text-sm"
            >
              <option value="">All Priorities</option>
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">New Work Order</h2>
              <button onClick={() => setShowCreateForm(false)} className="text-gray-400 hover:text-gray-600">
                <span className="text-xl">&times;</span>
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newOrder.title}
                  onChange={e => setNewOrder(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
                  placeholder="e.g., Prepare plot for burial"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newOrder.description}
                  onChange={e => setNewOrder(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
                  placeholder="Detailed instructions..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={newOrder.workOrderType}
                    onChange={e => setNewOrder(prev => ({ ...prev, workOrderType: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {WORK_ORDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newOrder.priority}
                    onChange={e => setNewOrder(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <select
                    value={newOrder.assignedTo}
                    onChange={e => setNewOrder(prev => ({ ...prev, assignedTo: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Unassigned</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={newOrder.dueDate}
                    onChange={e => setNewOrder(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700">Plots (optional)</label>
                  <button
                    type="button"
                    onClick={() => setIsPlotModalOpen(true)}
                    className="inline-flex items-center gap-1 text-xs text-brand-green-600 hover:text-brand-green-700"
                  >
                    <PlusIcon className="h-3 w-3" />
                    Add Plot
                  </button>
                </div>
                {formPlots.length > 0 ? (
                  <div className="border rounded-md divide-y text-sm">
                    {formPlots.map(plot => (
                      <div key={plot.fid} className="flex items-center justify-between px-3 py-2">
                        <span><MapPinIcon className="h-3.5 w-3.5 inline mr-1 text-gray-400" />{plot.alias || `Plot ${plot.fid}`}{plot.section ? ` (${plot.section})` : ''}</span>
                        <button type="button" onClick={() => setFormPlots(prev => prev.filter(p => p.fid !== plot.fid))} className="text-red-500 hover:text-red-700">
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No plots associated</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700">Contacts (optional)</label>
                  <button
                    type="button"
                    onClick={() => setIsContactModalOpen(true)}
                    className="inline-flex items-center gap-1 text-xs text-brand-green-600 hover:text-brand-green-700"
                  >
                    <PlusIcon className="h-3 w-3" />
                    Add Contact
                  </button>
                </div>
                {formContacts.length > 0 ? (
                  <div className="border rounded-md divide-y text-sm">
                    {formContacts.map(contact => (
                      <div key={contact.id} className="flex items-center justify-between px-3 py-2">
                        <span><UserIcon className="h-3.5 w-3.5 inline mr-1 text-gray-400" />{contact.firstName} {contact.lastName}</span>
                        <button type="button" onClick={() => setFormContacts(prev => prev.filter(c => c.id !== contact.id))} className="text-red-500 hover:text-red-700">
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No contacts associated</p>
                )}
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newOrder.includeMap}
                    onChange={e => setNewOrder(prev => ({ ...prev, includeMap: e.target.checked }))}
                    className="h-4 w-4 text-brand-green-600 rounded"
                  />
                  Include map
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newOrder.notifyOnStatusChange}
                    onChange={e => setNewOrder(prev => ({ ...prev, notifyOnStatusChange: e.target.checked }))}
                    className="h-4 w-4 text-brand-green-600 rounded"
                  />
                  Notify on status change
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 py-2 bg-brand-green-600 text-white rounded-md hover:bg-brand-green-700 disabled:bg-gray-300"
                >
                  {isLoading ? 'Creating...' : 'Create Work Order'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Work Orders List */}
      <div className="bg-white rounded-lg shadow divide-y">
        {isLoading && workOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : workOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
            <p>No work orders found</p>
          </div>
        ) : (
          workOrders.map(wo => {
            const priorityInfo = PRIORITIES.find(p => p.value === wo.priority);
            const typeInfo = WORK_ORDER_TYPES.find(t => t.value === wo.workOrderType);
            return (
              <div
                key={wo.id}
                onClick={() => setSelectedId(wo.id)}
                className="p-4 hover:bg-gray-50 cursor-pointer flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: wo.status?.color || '#6b7280' }}
                    />
                    <h3 className="font-medium text-gray-900 truncate">{wo.title}</h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span className={`px-1.5 py-0.5 rounded ${priorityInfo?.color || ''}`}>{priorityInfo?.label}</span>
                    <span>{typeInfo?.label}</span>
                    {wo.assignee && (
                      <span className="flex items-center gap-1">
                        <UserIcon className="h-3 w-3" />
                        {wo.assignee.firstName} {wo.assignee.lastName}
                      </span>
                    )}
                    {wo.dueDate && (
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />
                        {new Date(wo.dueDate).toLocaleDateString()}
                      </span>
                    )}
                    {(wo.workOrderPlots?.length > 0 || wo.plotId) && (
                      <span className="flex items-center gap-1">
                        <MapPinIcon className="h-3 w-3" />
                        {wo.workOrderPlots?.length > 0 ? `${wo.workOrderPlots.length} plot${wo.workOrderPlots.length > 1 ? 's' : ''}` : `Plot ${wo.plotId}`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className="text-xs px-2 py-1 rounded-full text-white"
                    style={{ backgroundColor: wo.status?.color || '#6b7280' }}
                  >
                    {wo.status?.name || 'Unknown'}
                  </span>
                  {user?.role !== 'grounds_crew' && (
                    <button
                      onClick={(e) => handleDelete(wo.id, e)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Plot Search Modal */}
      <PlotSearchModal
        open={isPlotModalOpen}
        onSelect={(plot) => {
          if (!formPlots.find(p => p.fid === plot.fid)) {
            setFormPlots(prev => [...prev, plot]);
          }
          setIsPlotModalOpen(false);
        }}
        onClose={() => setIsPlotModalOpen(false)}
      />

      {/* Contact Search Modal */}
      <ContactSearchModal
        open={isContactModalOpen}
        onSelect={(contact) => {
          if (!formContacts.find(c => c.id === contact.id)) {
            setFormContacts(prev => [...prev, contact]);
          }
          setIsContactModalOpen(false);
        }}
        onClose={() => setIsContactModalOpen(false)}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 rounded-md border disabled:opacity-50 hover:bg-gray-50"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-md border disabled:opacity-50 hover:bg-gray-50"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkOrders;
